import { useTheme } from '@/contexts/theme-provider';
import getOS from '@/lib/get-operating-system';
import { cn } from '@/lib/utils';
import { isArray } from 'lodash';
import {
  ArrowBigUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Command,
  Option,
} from 'lucide-react';
import { useMemo } from 'react';
import { Button } from './button';
import { PDKDeleteIcon } from './pdk-icons';

const KeyboardKeyIcon = ({
  children,
  className,
  variant: variantProp = 'light',
}: {
  children: React.ReactNode;
  className?: string;
  variant?: 'dark' | 'light';
}) => {
  const { theme } = useTheme();
  const variant = theme || variantProp;

  const os = getOS();
  const keysToDisplay = useMemo(() => {
    if (typeof children === 'string' && children.includes('+')) {
      const keys = children.split('+');

      return keys.map((key) => {
        if (
          key !== 'shift' &&
          key !== 'meta' &&
          key !== 'mod' &&
          key !== 'alt' &&
          key !== 'backspace'
        ) {
          return key.toLocaleUpperCase();
        }

        return (
          <div key={key} className="flex flex-row gap-1 items-center">
            {key === 'shift' && <ArrowBigUp className="size-2" />}
            {key === 'mod' &&
              (os === 'mac' ? (
                <Command className="p-0.5 w-0.5 h-0.5 text-xs" />
              ) : os === 'windows' ? (
                <div className="flex p-0.5 text-[6px]  items-center justify-center">
                  Ctrl
                </div>
              ) : (
                <div className="flex p-0.5 text-[6px]  items-center justify-center">
                  Mod
                </div>
              ))}
            {key === 'alt' && <Option className="p-0.75 size-2" />}
            {key === 'backspace' && <PDKDeleteIcon className="p-0.75 size-2" />}
          </div>
        );
      });
    }

    return [children];
  }, [children]);

  if (isArray(children)) {
    return [
      <KeyboardKeyIcon key={1}>{children[0].toUpperCase()}</KeyboardKeyIcon>,
      <Button
        key={2}
        variant="ghost"
        className={cn('h-2 w-fit px-1 text-xs', className, {
          'text-gray-500': variant === 'light',
          'bg-gray-900 text-white': variant === 'dark',
        })}
      >
        then
      </Button>,
      <KeyboardKeyIcon key={3}>{children[1].toUpperCase()}</KeyboardKeyIcon>,
    ];
  }

  return (
    <div className="flex flex-row gap-1 items-center">
      {keysToDisplay.map((key, index) => {
        return (
          <Button
            key={index}
            variant="outline"
            className={cn('h-2 px-2 text-xs', className, {
              'text-gray-500': variant === 'light',
              'bg-gray-900 text-white': variant === 'dark',
              'w-2': typeof key === 'string' && key.length === 1,
              'w-fit': typeof key === 'string' && key.length > 1,
            })}
          >
            {key === 'left' && (
              <ArrowLeft className="p-0.5 w-0.5 h-0.5 text-xs" />
            )}
            {key === 'right' && (
              <ArrowRight className="p-0.5 w-0.5 h-0.5 text-xs" />
            )}
            {key === 'up' && <ArrowUp className="p-0.5 w-0.5 h-0.5 text-xs" />}
            {key === 'down' && (
              <ArrowDown className="p-0.5 w-0.5 h-0.5 text-xs" />
            )}
            {key === 'Escape' && os === 'mac' && 'esc'}
            {key === 'Escape' && os === 'windows' && 'Esc'}
            {key === 'backspace' && <PDKDeleteIcon className="p-0.75 size-2" />}
            {key !== 'left' &&
              key !== 'right' &&
              key !== 'up' &&
              key !== 'down' &&
              key !== 'Escape' &&
              key !== 'backspace' &&
              key}
          </Button>
        );
      })}
    </div>
  );
};

export default KeyboardKeyIcon;
